// The ONE vocabulary for "what KIND of ödev is this row?".
//
// Since the Çalışmalar surface was folded into Ödevler, a single list / dashboard card /
// previous-work grid / calendar mixes FOUR kinds, and a learner must still be able to
// tell a şube ödevi from a destek ödevi at a glance. Every surface that names or icons a
// kind reads it from here, so the chip, the calendar entry and the practise filter row
// can never drift apart into three different words for the same thing.
//
// Deliberately NOT reusing TaskProgressIndicator's private SOURCE_STYLE: it is a
// gamification vocabulary, it hard-codes hex (the token rule forbids that on new code),
// and it only knows BRANCH / PERSONALIZED.

export type AssignmentKind =
  | "BRANCH_ASSIGNMENT"
  | "SUPPORT_ASSIGNMENT"
  | "PERSONALIZED_ASSIGNMENT"
  | "STANDARD_EXAM"
  // The two exam kinds of Raporlar → Sınavlar, whose merged list marks each row's kind with the same
  // chip the Ödevler rows carry. No content surface emits these types, so the homework lists and the
  // calendar are unaffected.
  | "HANDWRITTEN_EXAM"
  | "MOCK_EXAM";

export interface AssignmentKindDef {
  /** i18n key for the kind noun ("Şube ödevi", "Destek ödevi", …). */
  labelKey: string;
  /**
   * Canonical <Icon> name (see library/components/icons/iconNames.ts). All four are
   * canonical entries in the IconName union, so no alias or pack edit is needed.
   */
  iconName: string;
  /**
   * BEM-ish modifier suffix, emitted as a per-kind hook on the marker
   * (`akc-chip--support`). It carries NO colour: all four kinds render one achromatic
   * treatment and differentiate on `iconName` + label. Kept because it is a stable
   * per-kind selector for styling and tests.
   */
  modifier: string;
}

export const ASSIGNMENT_KIND_DEFS: Record<AssignmentKind, AssignmentKindDef> = {
  BRANCH_ASSIGNMENT: {
    labelKey: "assignment_kind_branch",
    iconName: "buildings",
    modifier: "branch",
  },
  SUPPORT_ASSIGNMENT: {
    labelKey: "assignment_kind_support",
    iconName: "users",
    modifier: "support",
  },
  PERSONALIZED_ASSIGNMENT: {
    labelKey: "assignment_kind_personalized",
    iconName: "user",
    modifier: "personalized",
  },
  STANDARD_EXAM: {
    labelKey: "assignment_kind_exam",
    iconName: "exam",
    modifier: "exam",
  },
  HANDWRITTEN_EXAM: {
    labelKey: "assignment_kind_handwritten_exam",
    iconName: "pencil",
    modifier: "handwritten-exam",
  },
  MOCK_EXAM: {
    labelKey: "assignment_kind_mock_exam",
    iconName: "listChecks",
    modifier: "mock-exam",
  },
};

export const isAssignmentKind = (type?: string | null): type is AssignmentKind =>
  !!type && Object.prototype.hasOwnProperty.call(ASSIGNMENT_KIND_DEFS, type);

/**
 * The definition for a content row's `type`, or `null` for anything this app does not
 * render as an ödev row (a duel, a contest, a future backend literal). Callers render
 * nothing rather than inventing a label — an unlabeled row beats a wrong one.
 */
export const assignmentKindDef = (
  type?: string | null,
): AssignmentKindDef | null =>
  isAssignmentKind(type) ? ASSIGNMENT_KIND_DEFS[type] : null;

export const assignmentKindLabelKey = (type?: string | null): string | null =>
  assignmentKindDef(type)?.labelKey ?? null;

export const assignmentKindIconName = (type?: string | null): string | null =>
  assignmentKindDef(type)?.iconName ?? null;
